import { useState, useEffect } from 'react';
import { validateImageUrl, getPlaceholderImage, preloadImage } from '../utils/imageUtils';

interface UseImageLoaderResult {
  isLoading: boolean;
  error: string | null;
  imageUrl: string;
}

export function useImageLoader(url: string, fallbackText?: string): UseImageLoaderResult {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState(getPlaceholderImage(fallbackText)); // Initialize with placeholder

  useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      if (!url) {
        if (isMounted) {
          setError('No image URL provided');
          setImageUrl(getPlaceholderImage(fallbackText));
          setIsLoading(false);
        }
        return;
      }

      if (!validateImageUrl(url)) {
        if (isMounted) {
          setError('Invalid image URL');
          setImageUrl(getPlaceholderImage(fallbackText));
          setIsLoading(false);
        }
        return;
      }

      try {
        // Try to load the image with a timeout
        const timeoutDuration = 10000; // 10 seconds timeout
        const imageLoadPromise = preloadImage(url);
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Image load timeout')), timeoutDuration);
        });

        await Promise.race([imageLoadPromise, timeoutPromise]);
        
        if (isMounted) {
          setImageUrl(url);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load image';
          console.error('Image load error:', errorMessage);
          setError(errorMessage);
          setImageUrl(getPlaceholderImage(fallbackText));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    setIsLoading(true);
    loadImage();

    return () => {
      isMounted = false;
    };
  }, [url, fallbackText]);

  return { isLoading, error, imageUrl };
}